import React from "react";
import { useNavigate } from "react-router-dom";
import TopNavBar from "./TopNavBar";
import Sidebar from "./Sidebar";

/**
 * The single chrome for every signed-in page: top bar, sidebar, content.
 *
 * Why this exists: TopNavBar used to be `position: fixed`, so it occupied no
 * layout space and every page had to remember `pt-16` to push its own content
 * clear of it. Roughly half of them didn't, which is what hid page content and
 * the first sidebar item behind the bar. Here the bar is a normal flex child,
 * so the offset is structural and no page can forget it.
 *
 * The page owns only what goes inside <main>; the scroll container lives here
 * so scrolling behaves identically on every route.
 */
export interface AppLayoutProps {
  /** Sidebar key to highlight, e.g. "patients" | "encounters" | "admin-doctors". */
  active?: string;
  children: React.ReactNode;
  /**
   * Padding for the content area. Defaults to the standard page padding; pass
   * "none" for full-bleed screens that manage their own spacing.
   */
  padding?: "default" | "none";
  /** Optional background override for the content area. */
  contentClassName?: string;
}

const AppLayout: React.FC<AppLayoutProps> = ({
  active,
  children,
  padding = "default",
  contentClassName = "",
}) => {
  const navigate = useNavigate();
  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  })();

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    navigate("/login");
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-gray-50">
      {/* In normal flow and non-shrinking: content can never sit underneath it. */}
      <TopNavBar user={storedUser} handleLogout={handleLogout} />

      {/* min-h-0 lets the row shrink so the children own the scrolling
          instead of the page growing past the viewport. */}
      <div className="flex flex-1 min-h-0">
        <Sidebar user={storedUser} active={active} />
        <main
          className={`flex-1 min-w-0 overflow-y-auto ${
            padding === "default" ? "p-8" : ""
          } ${contentClassName}`}
        >
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
